import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MapRef } from 'react-map-gl/maplibre'
import type * as GeoJSON from 'geojson'

import { initializeCogLayer, toggleCogVisibility } from '../../lib/cogHelpers'
import { registerCOGProtocol } from '../../lib/cogProtocol'
import { initializeImageryLayer, toggleImageryVisibility } from '../../lib/imageryHelpers'
import { registerPMTilesProtocol } from '../../lib/pmtilesProtocol'
import { MapAPI, MapSettings, MarkerOptions } from '../../lib/types'

interface MarkerData {
  id: string
  latitude: number
  longitude: number
  options?: MarkerOptions
}

/**
 * Хук для работы с MapLibre картами
 * Реализует императивный MapAPI интерфейс через GeoJSON Source/Layer подход
 */
export const useMapLibre = (
  settings: MapSettings,
  onReady?: (api: MapAPI) => void,
  onError?: (error: Error) => void
) => {
  const mapRef = useRef<MapRef>(null)

  const markersRef = useRef<Map<string, MarkerData>>(new Map())

  const [markersVersion, setMarkersVersion] = useState(0)

  const [isInitialized, setIsInitialized] = useState(false)

  // Деструктурируем настройки для стабильных зависимостей
  const centerLng = settings.center[0]
  const centerLat = settings.center[1]
  const zoom = settings.zoom

  useEffect(() => {
    registerPMTilesProtocol()
    registerCOGProtocol()
  }, [])

  const addMarker = useCallback(
    (id: string, latitude: number, longitude: number, options?: MarkerOptions) => {
      if (!isInitialized) {
        return
      }

      const existing = markersRef.current.get(id)
      if (
        existing &&
        existing.latitude === latitude &&
        existing.longitude === longitude &&
        JSON.stringify(existing.options) === JSON.stringify(options)
      ) {
        return // Не обновляем если данные те же
      }

      markersRef.current.set(id, { id, latitude, longitude, options })
      setMarkersVersion(v => v + 1)
    },
    [isInitialized]
  )

  const removeMarker = useCallback((id: string) => {
    if (markersRef.current.delete(id)) {
      setMarkersVersion(v => v + 1)
    }
  }, [])

  const clearMarkers = useCallback(() => {
    markersRef.current.clear()
    setMarkersVersion(v => v + 1)
  }, [])

  const setCenter = useCallback((latitude: number, longitude: number, zoom?: number) => {
    if (!mapRef.current) return

    mapRef.current.flyTo({
      center: [longitude, latitude],
      zoom,
      duration: 1000,
    })
  }, [])

  const setZoom = useCallback((zoom: number) => {
    if (!mapRef.current) return
    mapRef.current.setZoom(zoom)
  }, [])

  const getCenter = useCallback((): [number, number] | null => {
    if (!mapRef.current) return null
    const center = mapRef.current.getCenter()

    return [center.lat, center.lng]
  }, [])

  const getZoom = useCallback((): number | null => {
    return mapRef.current?.getZoom() ?? null
  }, [])

  const isReady = useCallback(() => isInitialized, [isInitialized])

  const markersGeoJSON = useMemo<GeoJSON.FeatureCollection<GeoJSON.Point>>(() => {
    const features: GeoJSON.Feature<GeoJSON.Point>[] = []

    markersRef.current.forEach(marker => {
      features.push({
        type: 'Feature',
        id: marker.id,
        geometry: {
          type: 'Point',
          coordinates: [marker.longitude, marker.latitude],
        },
        properties: {
          title: marker.options?.title || '',
          color: marker.options?.color || '#FF0000',
          icon: marker.options?.icon || 'marker-default',
        },
      })
    })

    return {
      type: 'FeatureCollection',
      features,
    }
  }, [markersVersion])

  /**
   * Переключает видимость COG-слоя
   * Доступно только если в настройках задан cogSource
   */
  const setCogVisible = useCallback(
    (visible: boolean) => {
      if (!settings.cogSource) {
        console.warn('COG source is not configured')

        return
      }

      const map = mapRef.current?.getMap()
      if (!map) {
        console.warn('Map is not initialized')

        return
      }

      try {
        toggleCogVisibility(map, settings.cogSource.layerId, visible)
      } catch (error) {
        console.error('Failed to toggle COG visibility:', error)
        onError?.(error as Error)
      }
    },
    [settings.cogSource, onError]
  )

  /**
   * Переключает видимость imagery-слоя
   * Доступно только если в настройках задан imageryLayer
   */
  const setImageryVisible = useCallback(
    (visible: boolean) => {
      if (!settings.imageryLayer) {
        console.warn('Imagery layer is not configured')

        return
      }

      const map = mapRef.current?.getMap()
      if (!map) {
        console.warn('Map is not initialized')

        return
      }

      try {
        // На всякий случай гарантируем, что источник и слой существуют.
        // initializeImageryLayer безопасен: сам проверяет наличие source/layer
        // и не создаёт дубликаты. Это защищает от редких случаев, когда
        // инициализация на onLoad по каким-то причинам не отработала.
        initializeImageryLayer(map, settings.imageryLayer)
        toggleImageryVisibility(map, settings.imageryLayer.layerId, visible)
      } catch (error) {
        console.error('Failed to toggle imagery visibility:', error)
        onError?.(error as Error)
      }
    },
    [settings.imageryLayer, onError]
  )

  // Мемоизируем MapAPI
  const mapApi = useMemo<MapAPI>(
    () => ({
      addMarker,
      removeMarker,
      clearMarkers,
      setCenter,
      setZoom,
      getCenter,
      getZoom,
      isReady,
      setCogVisible: settings.cogSource ? setCogVisible : undefined,
      setImageryVisible: settings.imageryLayer ? setImageryVisible : undefined,
    }),
    [
      addMarker,
      removeMarker,
      clearMarkers,
      setCenter,
      setZoom,
      getCenter,
      getZoom,
      isReady,
      setCogVisible,
      setImageryVisible,
      settings.cogSource,
      settings.imageryLayer,
    ]
  )

  useEffect(() => {
    if (isInitialized && mapApi) {
      onReady?.(mapApi)
    }
  }, [isInitialized, mapApi, onReady])

  /**
   * Инициализирует COG-источник при загрузке карты
   */
  const initializeCog = useCallback(
    (map: ReturnType<NonNullable<typeof mapRef.current>['getMap']>) => {
      if (!settings.cogSource) return

      try {
        initializeCogLayer(map, settings.cogSource)
      } catch (error) {
        console.error('Failed to initialize COG source:', error)
        onError?.(error as Error)
      }
    },
    [settings.cogSource, onError]
  )

  /**
   * Инициализирует imagery-источник при загрузке карты
   */
  const initializeImagery = useCallback(
    (map: ReturnType<NonNullable<typeof mapRef.current>['getMap']>) => {
      if (!settings.imageryLayer) return

      try {
        initializeImageryLayer(map, settings.imageryLayer)
      } catch (error) {
        console.error('Failed to initialize imagery layer:', error)
        onError?.(error as Error)
      }
    },
    [settings.imageryLayer, onError]
  )

  // Callback при загрузке карты
  const onMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap()

    if (map) {
      // Инициализируем COG, если он настроен
      initializeCog(map)
      // Инициализируем imagery, если он настроен
      initializeImagery(map)
    }

    setIsInitialized(true)
  }, [initializeCog, initializeImagery])

  useEffect(() => {
    return () => {
      markersRef.current.clear()
      setMarkersVersion(0)
      setIsInitialized(false)
    }
  }, [])

  return {
    mapRef,
    markersGeoJSON,
    onMapLoad,
    isInitialized,
  }
}
